import type * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../lib/cn.ts';
import { Logo, type LogoProps } from '../logo/logo.tsx';

/*
 * The rail's full width — DEV-36 pins it at 92px, 6px of air on either side.
 *
 * This constant feeds only the drawer's push threshold, which is arithmetic
 * and cannot be reached below 900px — the layout itself reads the `--rail-w`
 * token (theme.css), which narrows to 64px under the agenda breakpoint. The
 * two agree wherever both apply.
 */
export const RAIL_WIDTH = 92;

/*
 * One rail tile: the icon with its name under it, always visible. Exported as
 * variants rather than a component because the element is the consumer's to
 * choose — a router Link here, a plain anchor in Storybook — exactly as
 * buttonVariants is used for link-shaped buttons.
 *
 * The active tile is marked twice over: a white raised tile AND a green bar in
 * the gutter. Colour alone would not survive a greyscale print or a colour
 * vision deficiency.
 */
export const railLinkVariants = cva(
  [
    /* 6 + 20 icon + 4 gap + ~16 label + 6 = the 52px row DEV-36's rhythm is built on. */
    /*
     * No side padding: the label gets the full 80px the rail's gutter leaves it.
     * A 13-character name ("Conversations", "Organisations") measures 77px, and
     * the 2px inset was enough to cut it — an ellipsis on a nav label that
     * nearly fits is the worst of both.
     */
    'relative flex w-full flex-col items-center gap-1 rounded-xl pt-1.5 pb-1.5',
    'transition-colors',
    /* A tab that is there but held back, e.g. in a demo: still clickable, so it can say why. */
    'aria-disabled:opacity-40 aria-disabled:hover:bg-transparent',
    'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink-faint',
    /* The green bar sits in the gutter, on the rail's left edge. */
    'before:absolute before:-left-1.5 before:top-2.75 before:bottom-2.75 before:w-0.75',
    'before:rounded-r-xs before:content-[""]',
  ],
  {
    variants: {
      active: {
        true: 'bg-surface text-ink shadow-hover before:bg-accent',
        false: 'text-ink-muted hover:bg-fill hover:text-ink before:bg-transparent',
      },
    },
    defaultVariants: {
      active: false,
    },
  },
);

export type RailLinkVariants = VariantProps<typeof railLinkVariants>;

/*
 * The label under a rail icon.
 *
 * It carries no accessible name: the link holds an aria-label with the same
 * words, so the name is one string whatever the visuals do.
 */
export type RailLabelProps = Omit<React.ComponentProps<'span'>, 'style'>;

export function RailLabel({ className, ...props }: RailLabelProps) {
  return (
    <span
      data-slot='rail-label'
      aria-hidden='true'
      className={cn(
        /*
         * Under the agenda breakpoint the label goes and the rail narrows to
         * `--rail-w`'s icon width: the labels give way before the page content
         * does, never the other way round (DEV-36). The link keeps its
         * aria-label, so the name survives the label.
         */
        'max-w-full overflow-hidden text-xs font-medium text-ellipsis whitespace-nowrap',
        'max-agenda:hidden',
        className,
      )}
      {...props}
    />
  );
}

/*
 * The branding at the top of the rail: the square mark, centered like the
 * tiles under it. The mark alone — the track is a tile wide, no room for lettering,
 * and the mark already announces "Wundo".
 *
 * The height is the mark's, so the icons below it stay on their alignment line.
 */
export function RailLogo({ title, band }: Pick<LogoProps, 'title' | 'band'>) {
  return (
    <div data-slot='rail-logo' className='flex h-10 items-center justify-center'>
      <Logo title={title} band={band} />
    </div>
  );
}

export interface RailProps extends Omit<React.ComponentProps<'nav'>, 'style'> {
  /* Usually the Logo wrapped in a link home. */
  logo?: React.ReactNode;
  /* Pinned to the bottom: the account avatar. */
  footer?: React.ReactNode;
}

/*
 * The desktop navigation rail: icons with their names under them.
 *
 * The labels are on screen wherever there is room, which is what makes the
 * icons self-explanatory for a new user. Under the agenda breakpoint they give
 * way and the rail narrows to `--rail-w`'s icon width (DEV-36): whoever works
 * in half a screen already knows the four places, and the page content needs
 * the room more.
 *
 * Spacing is not decorative: pt-9 puts the logo on the same 36px line as the
 * page title, and the 54px offset below it drops the first tile onto the 130px
 * line where page bodies start.
 */
export function Rail({ logo, footer, className, children, ...props }: RailProps) {
  return (
    <nav
      data-slot='rail'
      /* No overflow clipping here: the active bar sits in the gutter, outside the tile. */
      className={cn(
        /*
         * The offset and the height both budget for the impersonation banner:
         * `--app-banner-h` is 0 unless the shell has one, in which case the
         * rail starts under it and gives up exactly its height — otherwise the
         * account disc at the foot is pushed off the bottom of the screen.
         */
        'sticky top-[var(--app-banner-h,0px)] flex h-[calc(100dvh-var(--app-banner-h,0px))] flex-col items-stretch px-1.5 pt-9 pb-4.5',
        'max-mobile:hidden',
        /*
         * The rail is a bounded pane — faint tint, hairline on its right edge —
         * because the content column centers in the space it leaves, and beside
         * an unbounded rail that centering reads as the page floating loose.
         * The hairline is an overlay rather than a border: a border would come
         * out of the 94px track that the tile geometry (track − padding) is
         * derived from.
         *
         * It anchors to the sticky box above, which is already positioned. No
         * `relative` here: it is the same utility group as `sticky`, so adding
         * it drops the stickiness and the whole rail scrolls off a long page.
         */
        'bg-fill-faint',
        'after:absolute after:inset-y-0 after:right-0 after:w-px after:bg-line after:content-[""]',
        className,
      )}
      {...props}
    >
      {logo}
      {/* 52px rows on 12px air: DEV-36's 64px rhythm. The offset still lands tile one on 130. */}
      <div className={cn('flex flex-col items-stretch gap-3', logo && 'mt-13.5')}>
        {children}
      </div>
      <div className='flex-1' />
      <div className='flex flex-col items-center'>{footer}</div>
    </nav>
  );
}
